/* eslint-disable react/no-array-index-key */
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Checkbox, Table } from '@vwfs-bronson/bronson-react';
import { dashboardSelectors } from '../../../../services/redux/features/dashboard.slice';

interface DashBoardTableProps {
  headers: string[];
  onSelection?: any;
  onDoubleClick?: any;
  onSelectAll?: any;
  values: any;
}

export const DashBoardTable = forwardRef<{ clearSelections: () => void }, DashBoardTableProps>(
  ({ headers, onSelection, onDoubleClick, onSelectAll, values }, ref) => {
    DashBoardTable.displayName = 'DashBoardTable';

    const { t } = useTranslation();
    const searchForm = useSelector(dashboardSelectors.selectDashboardSearchFormWithSelections);
    const [selectedIds, setSelectedIds] = useState<string[]>(searchForm.selectedCheckboxes ?? []);
    const [translatedHeaders, setTranslatedHeaders] = useState<string[]>([]);
    const oneClickTimeout = useRef<NodeJS.Timeout | number>(0);

    const lastTap = useRef<Record<string, number>>({});
    const DOUBLE_TAP_DELAY: number = 300;

    const handleCheckboxChange = (offerId: string) => {
      setSelectedIds((previousSelectedIds) =>
        previousSelectedIds.includes(offerId)
          ? previousSelectedIds.filter((id) => id !== offerId)
          : [...previousSelectedIds, offerId]
      );
    };

    const handleDbClick = (offerId: string) => {
      if (!onDoubleClick) return;
      clearTimeout(oneClickTimeout.current);
      setSelectedIds([offerId]);
      onDoubleClick(offerId);
    };

    const handleTouchStart = (offerId: string) => () => {
      const now = Date.now();
      const last = lastTap.current[offerId] || 0;
      if (now - last < DOUBLE_TAP_DELAY) {
        // double-tap detected
        handleDbClick(offerId);
        lastTap.current[offerId] = 0;
      } else lastTap.current[offerId] = now; // store first tap
    };

    const handleRowClick = async (offerId: string) => {
      const textSelection = window.getSelection();
      const noTextSelected = textSelection?.isCollapsed;
      clearTimeout(oneClickTimeout.current);
      oneClickTimeout.current = await setTimeout(() => {
        if (noTextSelected) {
          handleCheckboxChange(offerId);
        }
      }, 200);
    };

    useEffect(() => {
      const translated = headers.map((header: string) => t(`dashboard:table:headers:${header}`, { defaultValue: '' }));
      setTranslatedHeaders(translated);
    }, [headers, t]);

    useEffect(() => {
      if (!onSelection) return;
      onSelection(selectedIds);
    }, [selectedIds, onSelection]);

    const handleSelectAll = (e) => {
      if (e.currentTarget.checked) {
        const valuesIds = values.map((value) => value.offerId);
        setSelectedIds(valuesIds);
      } else {
        setSelectedIds([]);
      }

      if (onSelectAll) {
        onSelectAll(e.currentTarget.checked);
      }
    };

    // Expose the reset method to the parent
    useImperativeHandle(
      ref,
      () => ({
        clearSelections: () => {
          setSelectedIds([]);
        },
        setSelections: (ids: string[]) => {
          setSelectedIds(ids);
        },
      }),
      []
    );

    const isAllSelected = useMemo(() => {
      if (values.length && selectedIds.length) {
        return selectedIds.length === values.length;
      }
      return false;
    }, [values, selectedIds]);

    return (
      <Table id="dashBoardTable" tableHeader={translatedHeaders} wide>
        <Table.Thead>
          <Table.Tr>
            {translatedHeaders.map((header: string, index: number) => (
              <Table.Th className="u-ws-nowrap" key={header}>
                {translatedHeaders[index] === 'N° dossier' ? (
                  <>
                    <Checkbox checked={isAllSelected} onClick={(e) => e.stopPropagation()} onChange={handleSelectAll} />
                    <span> {header} </span>
                  </>
                ) : (
                  header
                )}
              </Table.Th>
            ))}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {values.map((row: any, index: number) => (
            <Table.Tr
              key={`${row?.offerId}-${index}`}
              selected={selectedIds.includes(row.offerId)}
              onClick={() => {
                handleRowClick(row?.offerId);
              }}
              onDoubleClick={() => handleDbClick(row?.offerId)}
              onTouchStart={handleTouchStart(row?.offerId)}
              className={`u-ws-nowrap ${row.runningContractStatus === 'Payé' ? 'u-bg-pale' : ''}`}
            >
              {headers.map((header: string, index: number) =>
                translatedHeaders[index] === 'N° dossier' ? (
                  <Table.Td key={`${header}.${index}.cell`}>
                    <Checkbox
                      checked={selectedIds.includes(row.offerId)}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => {
                        handleCheckboxChange(row.offerId);
                      }}
                    />
                    {row?.[header]}
                  </Table.Td>
                ) : (
                  <Table.Td key={`${header}.${index}.cell`} style={{ userSelect: 'text' }}>
                    {row?.[header]}
                  </Table.Td>
                )
              )}
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    );
  }
);
